import { useRouter } from 'next/router';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  QueryKey,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from 'react-query';

import { AxiosError } from 'axios';
import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';
import chunk from 'lodash/chunk';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';

import { useGlobalContext } from '@contexts/global';

import { fetchUserMarketItemInventories } from '../api/common';
import {
  ISetting,
  UserSettingKeys,
  fetchCertification,
  fetchFanCheck,
  fetchMe,
  fetchMyMarketItemInventories,
  fetchUserSettings,
} from '../api/user';
import {
  CertificationType,
  MarketItemType,
  QUERY_KEYS,
  SettingType,
} from '../constants';
import { useAuthStore } from '../contexts/auth';
import {
  IMarketItemCommentResource,
  IMarketItemProfileBgResource,
  IMarketItemProfileBtnResource,
  IMarketItemUserNicknameResource,
  IMarketItemUserPictureResource,
  IOtherUserMarketItemInventory,
  ISimpleUser,
  IUserMe,
} from '../model';
import { isAfter } from './date';
import { HistoryManager, jsonStringify } from './misc';
import { useWebView } from './webview';

/** API */

export function useMe(options?: UseQueryOptions<IUserMe>) {
  const { status } = useAuthStore();

  const queryKey = QUERY_KEYS.USER.ME;
  const query = useQuery<IUserMe>(queryKey, () => fetchMe(), {
    staleTime: minutesToMilliseconds(60),
    cacheTime: minutesToMilliseconds(60),
    suspense: false,
    enabled: status === 'success',
    ...options,
  });

  return query;
}

/**
 * 로그인이 된 페이지에서만 사용
 */
export function useAdultCertifaction() {
  const query = useQuery(
    QUERY_KEYS.CERTIFICATION.ADULT,
    () => fetchCertification(CertificationType.adult),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      suspense: false,
      select: (data) => data.ageGrade === 19,
    }
  );

  return query;
}

export function usePhoneCertifaction() {
  const queryClient = useQueryClient();
  const initialData = {
    type: CertificationType.phone,
    phoneNumber: '',
    ageGrade: 0,
    ageGraded: 0,
  };
  const query = useQuery(
    QUERY_KEYS.CERTIFICATION.PHONE,
    () => fetchCertification(CertificationType.phone),
    {
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0,

      onError(err) {
        if ((err as AxiosError).response?.status === 404) {
          queryClient.setQueryData(QUERY_KEYS.CERTIFICATION.PHONE, initialData);
        }
      },
    }
  );

  return query;
}

const bulkFetch = <T>(
  fetcher: (ids: number[]) => Promise<T>,
  ids: number[]
) => {
  const idChunks = chunk(ids, 30);
  return Promise.all(idChunks.map(fetcher)).then((chunks) => {
    return flatMap(chunks) as unknown as T;
  });
};

export function useUserRelatedCache<T extends { id: number }>(
  groupQueryKey: QueryKey,
  userables: (ISimpleUser | undefined)[],
  {
    fetcher,
    enabled = true,
  }: {
    fetcher: (userIds: number[]) => Promise<T[]>;
    enabled?: boolean;
  }
) {
  const [, setUpdated] = useState<number>();
  const isPending = useRef(false);

  const queryClient = useQueryClient();

  const users = userables.filter(
    (user) => user !== undefined && user?.isActive !== false
  ) as ISimpleUser[];

  const cachedUserIds = queryClient
    .getQueryCache()
    .findAll(groupQueryKey)
    .map((q) => q.queryKey[q.queryKey.length - 1]);

  const newUserIds = [
    ...new Set(
      users
        .filter((user) => !cachedUserIds.includes(user.id))
        .map((user) => user.id)
    ),
  ];

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isPending.current) {
      return;
    }

    if (newUserIds.length === 0) {
      return;
    }

    isPending.current = true;

    bulkFetch(fetcher, newUserIds)
      .then((items) => {
        newUserIds.forEach((newUserId) => {
          const individualQueryKey = isArray(groupQueryKey)
            ? [...groupQueryKey, newUserId]
            : [groupQueryKey, newUserId];
          queryClient.setQueryData(
            individualQueryKey,
            items.find((item) => item.id === newUserId)
          );
          queryClient.setQueryDefaults(individualQueryKey, {
            staleTime: Infinity,
            cacheTime: Infinity,
          });
        });

        setUpdated(new Date().getTime());
      })
      .finally(() => {
        isPending.current = false;
      });
  }, [newUserIds, enabled]);
}

export function useUserMarketItemInventories(
  userables: (ISimpleUser | undefined)[]
) {
  const { isCheming } = useGlobalContext();
  const queryKey = QUERY_KEYS.MARKET_ITEM_INVENTORY.LIST;
  return useUserRelatedCache(queryKey, userables, {
    fetcher: fetchUserMarketItemInventories,
    enabled: !isCheming,
  });
}

type _MarketItem = {
  [MarketItemType.userPicture]: IMarketItemUserPictureResource;
  [MarketItemType.userNickname]: IMarketItemUserNicknameResource;
  [MarketItemType.profileBg]: IMarketItemProfileBgResource;
  [MarketItemType.profileBtn]: IMarketItemProfileBtnResource;
  [MarketItemType.comment]: IMarketItemCommentResource;
};

export function useUserMarketItemInventory(id?: number) {
  const queryKey = QUERY_KEYS.MARKET_ITEM_INVENTORY.ONE(id);
  const { isCheming } = useGlobalContext();

  const queryClient = useQueryClient();
  const data =
    queryClient.getQueryData<IOtherUserMarketItemInventory>(queryKey);

  const getDecoration = useCallback(
    <K extends keyof _MarketItem>(
      inventory: IOtherUserMarketItemInventory | undefined,
      type: K
    ): _MarketItem[K] | undefined => {
      if (!inventory) {
        return undefined;
      }

      return inventory.marketItems.find((item) => item.type === type)
        ?.resource as _MarketItem[K];
    },
    []
  );

  if (isCheming) {
    return { data: undefined, getDecoration: () => undefined };
  }

  return { data, getDecoration };
}

export function useFanList(
  userables: (ISimpleUser | undefined)[],
  targetUserId?: number
) {
  const queryKey = QUERY_KEYS.FAN.LIST(targetUserId);
  return useUserRelatedCache(queryKey, userables, {
    fetcher: (userIds) => fetchFanCheck(targetUserId!, userIds),
    enabled: !!targetUserId,
  });
}

type IFanCheck = {
  id: number;
  isFan: boolean;
  fanClubName: string | null;
};

export function useFan(userId: number, targetUserId?: number) {
  const queryClient = useQueryClient();

  if (!targetUserId) {
    return { data: undefined };
  }

  const queryKey = QUERY_KEYS.FAN.LIST(targetUserId);

  const data = queryClient.getQueryData<IFanCheck>([...queryKey, userId]);

  return { data };
}

export function useAdRemove() {
  const { status } = useAuthStore();

  const query = useQuery(
    QUERY_KEYS.MY_MARKET_ITEM_INVENTORY.LIST,
    fetchMyMarketItemInventories,
    {
      enabled: status === 'success',
      suspense: false,
      staleTime: minutesToMilliseconds(30),
      cacheTime: minutesToMilliseconds(30),
    }
  );
  const adRemove = query.data?.find(
    (item) => item.marketItem.code === 'etc_ad_remove'
  );

  return adRemove && adRemove.expired
    ? isAfter(adRemove.expired, new Date())
    : false;
}

export function useNotification(
  objectType: SettingType,
  objectId: number,
  settingKey: keyof typeof UserSettingKeys,
  enabled = true
) {
  const queryClient = useQueryClient();

  const initialData = {
    id: String(objectId),
    setting: {
      [settingKey]:
        settingKey === 'newEpisodePushStatus' ||
        settingKey === 'newGroundPushStatus'
          ? 'default'
          : 'partial_enable',
    },
  } as ISetting;

  const queryKey = QUERY_KEYS.SETTING.ONE(objectType, objectId);
  const query = useQuery(
    queryKey,
    () => fetchUserSettings(objectType, objectId),
    {
      suspense: false,
      staleTime: minutesToMilliseconds(15),
      cacheTime: minutesToMilliseconds(15),
      enabled,
      onError: () => {
        queryClient.setQueryData(queryKey, initialData);
      },
    }
  );

  return query;
}

/** Util */

export function useScreen(screenName: string) {
  const router = useRouter();

  const { web } = useWebView();

  useEffect(() => {
    web.data('screen', { screenName, parameters: jsonStringify(router.query) });
  }, [router]);
}

export function useHistory() {
  const { current } = useRef(HistoryManager.getInstance());
  return current;
}
